import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Typography,
  Autocomplete,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import countryList from "react-select-country-list";
import { isEmpty } from "lodash";

const PLATFORM_OPTIONS = [
  { value: "", label: "ALL" },
  { value: "android", label: "ANDROID" },
  { value: "ios", label: "IOS" },
  { value: "android,ios", label: "ANDROID,IOS" },
];

const SCREEN_OPTIONS = [
  { value: "", label: "ALL" },
  { value: "belanja", label: "BELANJA" },
  { value: "home", label: "HOME" },
  { value: "as2in1-home", label: "AS2IN1 HOME" },
  { value: "as2in1-postpaid-home", label: "AS2IN1 POSTPAID HOME" },
  { value: "as2in1-belanja", label: "AS2IN1 BELANJA" },
  { value: "as2in1-postpaid-belanja", label: "AS2IN1 POSTPAID BELANJA" },
  { value: "test-home", label: "TEST HOME" },
  { value: "test-belanja", label: "TEST BELANJA" },
];

const countryOptions = countryList()
  .getData()
  .map((c) => ({
    label: `${c.label} (${c.value})`,
    value: c.value,
  }));

export default function FilterLayout({
  onChange,
}: {
  onChange: (filters: any) => void;
}) {
  const filter = JSON.parse(localStorage.getItem("filter-layout") || "{}");
  const [purpose, setPurpose] = useState<string>(filter.purpose || "");
  const [label, setLabel] = useState<string>(filter.label || "");
  const [labelEng, setLabelEng] = useState<string>(filter.labelEng || "");
  const [platform, setPlatform] = useState<string>(filter.platform || "");
  const [screen, setScreen] = useState<string>(filter.screen || "");
  const [worldwide, setWorldwide] = useState<string>(filter.worldwide || "");
  const [include, setInclude] = useState<string[]>(filter.inclued || []);
  const [exclude, setExclude] = useState<string[]>(filter.exclude || []);

  function isEmptyObject(obj: any) {
    return (
      (obj.purpose === "" &&
        obj.label === "" &&
        obj.labelEng === "" &&
        obj.platform === "" &&
        obj.screen === "" &&
        obj.worldwide === "" &&
        obj.include.length === 0 &&
        obj.exclude.length === 0) ||
      isEmpty(obj)
    );
  }

  const triggerChange = (extra = {}) => {
    const input = {
      purpose,
      label,
      labelEng,
      platform,
      screen,
      worldwide,
      include,
      exclude,
      ...extra,
    };
    localStorage.setItem("filter-layout", JSON.stringify({ ...input }));
    onChange(input);
  };

  return (
    <Accordion
      style={{ marginBottom: 10 }}
      defaultExpanded={!isEmptyObject(filter)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">
          <b>Filters</b>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Purpose"
              value={purpose}
              onChange={(e) => {
                setPurpose(e.target.value);
                triggerChange({ purpose: e.target.value });
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Label Indonesia"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                triggerChange({ label: e.target.value });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Label English"
              value={labelEng}
              onChange={(e) => {
                setLabelEng(e.target.value);
                triggerChange({ labelEng: e.target.value });
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platform}
                label="Platform"
                onChange={(e: SelectChangeEvent) => {
                  setPlatform(e.target.value);
                  triggerChange({ platform: e.target.value });
                }}
              >
                {PLATFORM_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Screen</InputLabel>
              <Select
                value={screen}
                label="Screen"
                onChange={(e: SelectChangeEvent) => {
                  setScreen(e.target.value);
                  triggerChange({ screen: e.target.value });
                }}
              >
                {SCREEN_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Country Mode</InputLabel>
              <Select
                value={worldwide}
                label="Country Mode"
                onChange={(e: SelectChangeEvent) => {
                  const val = e.target.value;
                  setWorldwide(val);
                  setInclude([]);
                  setExclude([]);
                  triggerChange({ worldwide: val, include: [], exclude: [] });
                }}
              >
                <MenuItem value={""}>ALL</MenuItem>
                <MenuItem value={"true"}>Worldwide</MenuItem>
                <MenuItem value={"false"}>Selected Country</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {worldwide === "false" && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={countryOptions}
                getOptionLabel={(option) => option.label}
                value={countryOptions.filter((c) => include.includes(c.value))}
                onChange={(_, newValue) => {
                  const val = newValue.map((c) => c.value);
                  setInclude(val);
                  triggerChange({ include: val });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Include Countries" />
                )}
              />
            </Grid>
          )}

          {worldwide === "true" && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={countryOptions}
                getOptionLabel={(option) => option.label}
                value={countryOptions.filter((c) => exclude.includes(c.value))}
                onChange={(_, newValue) => {
                  const val = newValue.map((c) => c.value);
                  setExclude(val);
                  triggerChange({ exclude: val });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Exclude Countries" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
